/**
 * Boot sequence.
 *
 * Config resolves before React mounts, and the resolved mode decides which
 * application exists at all. Render-then-decide would flash the wrong brand and,
 * worse, briefly mount console routes on a tenant domain.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { loadConfig } from './shared/lib/config';
import { loadSession, ANONYMOUS } from './shared/lib/session';
import { setUnauthenticatedHandler } from './shared/lib/openedx';
import { SuperadminApp } from './superadmin/SuperadminApp';
import { TenantApp } from './TenantApp';
import './shared/design/tokens.css';
import './shared/design/base.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

async function boot() {
  const root = createRoot(document.getElementById('root')!);

  // Fired only after a JWT refresh has already failed, so it means "signed out" rather
  // than "one request 401'd". Guarded against re-entry: several in-flight requests can
  // each fail at once, and three simultaneous redirects lose the `next` parameter.
  let redirecting = false;
  setUnauthenticatedHandler(() => {
    if (redirecting) return;
    redirecting = true;
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?next=${next}`);
  });

  /*
   * BOTH STARTED HERE, THEN AWAITED — nose to tail is what this used to be, and the two
   * requests have nothing to say to each other.
   *
   * `loadConfig()` asks `/api/tenant/v1/tenants/by-host/{host}` with the browser's own
   * hostname and reads no session. `loadSession()` asks `/api/user/v1/me` and reads no
   * config: `lmsOrigin` is a build-time env var and `openedx.ts` imports nothing from
   * `config.ts`, so there is no hidden ordering between them. Checked, because a wrong
   * answer here is a boot that works locally and races in production.
   *
   * WHAT MUST STAY SEQUENTIAL, and does: `loadSession` itself is two requests in order —
   * `/api/user/v1/me`, then `/api/tenant/v1/users/{username}/roles` — because the second is
   * addressed by the username the first returns. That is inside `loadSession` and untouched.
   *
   * The awaits stay in this order for the same reason they were written this way: a config
   * failure is fatal and renders instead of the app, so it is settled first. The session
   * promise carries its own `.catch`, so abandoning it on that path cannot become an
   * unhandled rejection.
   */
  const configLoading = loadConfig();
  // A failed session lookup is not fatal for a tenant site — it still works signed
  // out. The console handles its own unauthenticated state.
  const sessionLoading = loadSession().catch(() => ANONYMOUS);

  let config;
  try {
    config = await configLoading;
  } catch (error) {
    root.render(<FatalError error={error} />);
    return;
  }

  const session = await sessionLoading;

  /*
   * SEED THE CACHE WITH THE SESSION WE ALREADY HAVE.
   *
   * Boot fetches the session before React mounts, because the resolved mode decides which
   * application exists at all. That fetch happened OUTSIDE the QueryClient, so nothing
   * landed in its cache — and then every screen independently ran
   * `useQuery({ queryKey: ['session'], queryFn: loadSession })`, found an empty cache, and
   * fetched the whole thing again. `loadSession` issues TWO sequential requests
   * (`/api/user/v1/me`, then `/api/tenant/v1/users/{username}/roles`), so each screen
   * repeated both.
   *
   * MEASURED: `/api/user/v1/me` and the roles call were each fetched TWICE on 9 of the 16
   * pages measured, and the duplicate pair cost up to 6715ms on `/paths/{key}` where the
   * server was already contended.
   *
   * The key must match the screens' key EXACTLY — `['session']`, the literal used by all
   * twelve call sites. A mismatch here fails silently: nothing errors, the cache simply
   * stays cold and the duplicates come back, which is why it is a bare constant on both
   * sides rather than something computed.
   */
  queryClient.setQueryData(['session'], session);

  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        {config.mode === 'platform' ? (
          <SuperadminApp session={session} />
        ) : (
          <TenantApp session={session} />
        )}
      </QueryClientProvider>
    </StrictMode>,
  );
}

function FatalError({ error }: { error: unknown }) {
  return (
    <main className="page page--message">
      <h1>This site is not available right now</h1>
      <p>
        We could not work out which academy this address belongs to, so nothing can
        load. This usually means the domain has not been registered yet.
      </p>
      <p className="page__detail">{error instanceof Error ? error.message : String(error)}</p>
    </main>
  );
}

void boot();
